import { ExtractionDetailsRequest, IWhitePaperReportChanges, ProblemDetails, Report } from '../../model';
import { ErrorType } from '../../utils/UseCustomInstance';
import { IEnterAmountsDialogProps } from '../enter-amounts-dialog/EnterAmountsDialog';
import { IJournalEntryDialogProps } from './JournalEntryDialog';
import { AccountAmountWhitePaperRow, ITrackedExtractionDetailsAccountSummary } from './ReportDrillDownUtils';

export enum DrillDownActionType {
    ChangeActiveTabIndex = 0,
    StartProcessingExport = 1,
    ExportFailure = 8,
    StopProcessingExport = 2,
    StartProcessingSaveAmounts = 3,
    SaveAmountsSuccess = 4,
    SaveAmountsFailure = 5,
    SaveAmountsComplete = 6,
    AmountEdited = 7,
    RequestCellAuditStart = 9,
    RequestCellAuditSuccess = 10,
    RequestCellAuditFailure = 11,
    RequestCellAuditEnd = 12,
    ConfirmationDialogOpened = 13,
    ConfirmationDialogClosed = 14,
    EnterAmountsDialogOpened = 15,
    EnterAmountsDialogClosed = 16,
    JournalEntryDialogOpened = 17,
    JournalEntryDialogClosed = 18,
    AccountAmountExpandChanged = 19,
    TraceCalculationExpandChanged = 20,
}

export interface IEnterAmountsOptionalProps
    extends Omit<
        IEnterAmountsDialogProps,
        | 'taxReturnKey'
        | 'show'
        | 'saveButtonIsDisabled'
        | 'updateState'
        | 'rowDescription'
        | 'drillDownRowNumber'
        | 'columnId'
        | 'changeTrackingData'
    > {}

export interface IAmountEditedDrillDownAction {
    columnId: number;
    event: any;
    item: AccountAmountWhitePaperRow;
    report: Report;
    rowNumber: number;
    type: DrillDownActionType.AmountEdited;
}

export interface IEnterAmountsDialogClosedDrillDownAction {
    type: DrillDownActionType.EnterAmountsDialogClosed;
    updatedChangeTrackingData: IWhitePaperReportChanges;
    amountsChangedOnServer?: boolean;
}

export interface IEnterAmountsDialogOpenedDrillDownAction {
    enterAmountsDialogProps: IEnterAmountsOptionalProps;
    // optional row key identifying the summary/row which opened the dialog
    summaryKey?: string;
    type: DrillDownActionType.EnterAmountsDialogOpened;
}

export interface IJournalEntryDialogOpenedDrillDownAction {
    type: DrillDownActionType.JournalEntryDialogOpened;
    journalEntryDialogProps: IJournalEntryDialogProps;
}

export interface IJournalEntryDialogClosedDrillDownAction {
    type: DrillDownActionType.JournalEntryDialogClosed;
}

export interface IAccountAmountExpandChangedDrillDownAction {
    type: DrillDownActionType.AccountAmountExpandChanged;
    openIds: Set<string | number>;
}

export interface ITraceCalculationExpandChangedDrillDownAction {
    type: DrillDownActionType.TraceCalculationExpandChanged;
    uiKey?: string;
    isOpening?: boolean;
}

// FIXME: We can get much better type checking if we added types for each value of DrillDownActionType and then defined DrillDownAction as a union type of all those types.
//        However, since there are 17 values for DrillDownActionType, this would be a lot of work.
//        So, using Exclude here allows us to avoid having to do all that work up front while still gaining that benefit for any new enum values.
export interface IGenericDrillDownAction {
    error?: ErrorType<ProblemDetails> | null;
    event?: any;
    eventTargetTabIndex?: number;
    extractionDetailsRequests?: ExtractionDetailsRequest[];
    initialAccountAmountSummaries?: ITrackedExtractionDetailsAccountSummary[];
    report?: Report;
    rowNumber?: number;
    columnId?: number;
    item?: AccountAmountWhitePaperRow;
    newActiveTab?: number;
    type: Exclude<DrillDownActionType, DrillDownActionType.AmountEdited> &
        Exclude<DrillDownActionType, DrillDownActionType.EnterAmountsDialogOpened> &
        Exclude<DrillDownActionType, DrillDownActionType.EnterAmountsDialogClosed> &
        Exclude<DrillDownActionType, DrillDownActionType.JournalEntryDialogOpened> &
        Exclude<DrillDownActionType, DrillDownActionType.JournalEntryDialogClosed> &
        Exclude<DrillDownActionType, DrillDownActionType.AccountAmountExpandChanged> &
        Exclude<DrillDownActionType, DrillDownActionType.TraceCalculationExpandChanged>;
    updatedChangeTrackingData?: IWhitePaperReportChanges;
}

export type DrillDownAction =
    | IAmountEditedDrillDownAction
    | IGenericDrillDownAction
    | IEnterAmountsDialogClosedDrillDownAction
    | IEnterAmountsDialogOpenedDrillDownAction
    | IJournalEntryDialogOpenedDrillDownAction
    | IJournalEntryDialogClosedDrillDownAction
    | IAccountAmountExpandChangedDrillDownAction
    | ITraceCalculationExpandChangedDrillDownAction;
